/**
 * The metric grid for a refresh run.
 *
 * The scrape grid counts scrape outcomes and the enrichment grid counts enrichment outcomes;
 * neither vocabulary fits here. A refresh job settles `unchanged` (the healthy majority — one
 * fetch, no LLM, nothing to do), `updated`, `hitl`, `bad_fetch`/`not_listed`, `shadow`,
 * `retired`, `dismissed` or `failed`, and `v_run_summary` can see exactly two of those:
 * `hitl_count` (`final_outcome='manual_review'`, which a park does stamp) and `failed_count`.
 * `created_count`/`duplicate_count` are structurally zero, and `skipped_count` misses every
 * refresh skip because those carry a `refresh_*` `final_outcome` rather than `'skipped'`.
 *
 * So the tiles read `GET /refresh-jobs/summary` instead — one server-side `GROUP BY`, because a
 * sweep hour is thousands of jobs and the enrichment page's fetch-every-row trick does not
 * survive that.
 *
 * **Clicking a tile filters the table by `refresh_outcome`**, which is a real route param, so
 * unlike the enrichment grid (whose clicks approximate an outcome with a `status`) every
 * clickable tile here narrows to exactly the rows it counts. `Jobs` clears the filter; `Cost`
 * opens the breakdown dialog.
 */
import {
  AlertTriangle,
  CheckCircle2,
  CircleDollarSign,
  Equal,
  EyeOff,
  ListChecks,
  ShieldQuestion,
  XCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import { MetricCard, shareOf } from "@/pages/tina/runs/components/metric-card";
import {
  inFlightCount,
  outcomeCount,
} from "@/pages/tina/runs/lib/refresh-jobs-filters";
import type { RefreshJobsSummary, RefreshOutcome } from "@/types/tina";

interface RefreshRunMetricsProps {
  /** Null until the summary lands. */
  summary: RefreshJobsSummary | null;
  outcomeFilter: RefreshOutcome | null;
  onToggleOutcome: (outcome: RefreshOutcome) => void;
  onClearOutcome: () => void;
  onCostClick: () => void;
}

const dash = "—";

export function RefreshRunMetrics({
  summary,
  outcomeFilter,
  onToggleOutcome,
  onClearOutcome,
  onCostClick,
}: RefreshRunMetricsProps) {
  const total = summary?.total ?? 0;
  const outcomes = summary?.outcomes ?? [];
  const unchanged = outcomeCount(outcomes, "unchanged");
  const updated = outcomeCount(outcomes, "updated");
  const parked = outcomeCount(outcomes, "hitl");
  const shadow = outcomeCount(outcomes, "shadow");
  // One tile, because a curator chases them for the same reason: the evidence
  // never arrived. Tina counts them against one `bad_fetch_streak` too, and
  // three in a row is what raises `page_gone`.
  const noEvidence =
    outcomeCount(outcomes, "bad_fetch") + outcomeCount(outcomes, "not_listed");
  const failed = outcomeCount(outcomes, "failed");
  const inFlight = inFlightCount(outcomes);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
      <MetricCard
        label="Jobs"
        value={summary ? total : dash}
        icon={<ListChecks className="h-3.5 w-3.5" />}
        tone="sky"
        hint={
          summary
            ? inFlight > 0
              ? `${inFlight} still in flight`
              : `${summary.proposal_count} proposed · ${summary.applied_count} written`
            : undefined
        }
        onClick={outcomeFilter ? onClearOutcome : undefined}
        active={outcomeFilter === null}
      />
      <MetricCard
        label="Unchanged"
        value={summary ? unchanged : dash}
        icon={<Equal className="h-3.5 w-3.5" />}
        tone="zinc"
        share={summary ? shareOf(unchanged, total) : null}
        hint="one fetch, no LLM"
        onClick={() => onToggleOutcome("unchanged")}
        active={outcomeFilter === "unchanged"}
      />
      <MetricCard
        label="Updated"
        value={summary ? updated : dash}
        icon={<CheckCircle2 className="h-3.5 w-3.5" />}
        tone="emerald"
        share={summary ? shareOf(updated, total) : null}
        hint="written to the live event"
        onClick={() => onToggleOutcome("updated")}
        active={outcomeFilter === "updated"}
      />
      <MetricCard
        label="Needs review"
        value={summary ? parked : dash}
        icon={<ShieldQuestion className="h-3.5 w-3.5" />}
        tone="orange"
        share={summary ? shareOf(parked, total) : null}
        hint="waiting on a curator"
        onClick={() => onToggleOutcome("hitl")}
        active={outcomeFilter === "hitl"}
      />
      <MetricCard
        label="No evidence"
        value={summary ? noEvidence : dash}
        icon={<AlertTriangle className="h-3.5 w-3.5" />}
        tone="amber"
        share={summary ? shareOf(noEvidence, total) : null}
        // Not clickable: the number is two outcomes added together and the
        // route takes one value, so a click would silently show half of it.
        // The Outcome column filter reaches either on its own.
        hint="bad fetch or unlisted"
      />
      <MetricCard
        label={shadow > 0 ? "Shadow" : "Failed"}
        value={summary ? (shadow > 0 ? shadow : failed) : dash}
        icon={
          shadow > 0 ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <XCircle className="h-3.5 w-3.5" />
          )
        }
        tone={shadow > 0 ? "amber" : "red"}
        share={summary ? shareOf(shadow > 0 ? shadow : failed, total) : null}
        // The two swap because they are mutually exclusive in practice and the
        // grid has seven slots: with `TINA_REFRESH_SHADOW` on, every job is
        // `shadow` and a Failed tile reads 0 forever; with it off, no job is.
        // Shadow wins when present, because a run of them means the kill-switch
        // is still on and nothing was written.
        hint={shadow > 0 ? "nothing was written" : undefined}
        onClick={() => onToggleOutcome(shadow > 0 ? "shadow" : "failed")}
        active={outcomeFilter === (shadow > 0 ? "shadow" : "failed")}
      />
      <MetricCard
        label="Cost"
        value={
          <span className={cn(!summary?.cost && "text-muted-foreground")}>
            {formatUsdCost(summary?.cost)}
          </span>
        }
        icon={<CircleDollarSign className="h-3.5 w-3.5" />}
        tone="indigo"
        hint="click for breakdown"
        onClick={onCostClick}
      />
    </div>
  );
}
